import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core'
import { Router } from '@angular/router'
import { AdminClient, CrmAssignee, LeadClosedReason, LeadStatus } from '@parkupp/core'
import { AdminClientService } from 'src/app/services/admin-client.service'
import { GlobalService } from 'src/app/services/global.service'
import { GlobalVariablesService } from 'src/app/services/global-variables.service'
import Swal from 'sweetalert2'

@Component({
    selector: 'app-parker-table',
    templateUrl: './parker-table.component.html',
    styleUrls: ['./parker-table.component.scss'],
})
export class ParkerTableComponent implements OnInit, OnChanges {
    @Input() filteredAdminClients: AdminClient[]
    @Input() visibleColumns: { [key: string]: boolean } = {}
    @Input() sortColumn: string = ''
    @Input() sortDirection: 'asc' | 'desc' = 'asc'
    @Input() crmAssignees: CrmAssignee[] = []
    @Input() crmAssigneeMap: { [key: string]: CrmAssignee } = {}
    @Input() isSalesManagerMode = false
    @Output() leadClick = new EventEmitter<AdminClient>()
    @Output() sortChange = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>()

    leadStatuses = Object.values(LeadStatus)

    constructor(public globalService: GlobalService, private adminClientService: AdminClientService, private router: Router, public globalVariablesService: GlobalVariablesService) {}

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        // Apply sort when data changes and there's a sortColumn set
        if (changes['filteredAdminClients'] && this.sortColumn && this.filteredAdminClients?.length) {
            this.applySort()
        }
    }

    applySort(): void {
        if (!this.sortColumn || !this.filteredAdminClients) return

        const column = this.sortColumn
        this.filteredAdminClients.sort((a: any, b: any) => {
            // Always sort starred items to the top
            if (a.isStarred && !b.isStarred) return -1
            if (!a.isStarred && b.isStarred) return 1

            const valueA = a[column]
            const valueB = b[column]

            if (valueA < valueB) {
                return this.sortDirection === 'asc' ? -1 : 1
            } else if (valueA > valueB) {
                return this.sortDirection === 'asc' ? 1 : -1
            } else {
                return 0
            }
        })
    }

    sortData(column: string) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            this.sortColumn = column
            this.sortDirection = 'asc'
        }

        // Emit sort change to parent
        this.sortChange.emit({ column: this.sortColumn, direction: this.sortDirection })

        this.applySort()
    }
    async onContextMenu(event: MouseEvent, adminClient: AdminClient) {
        this.globalService.contextMenuItems = []
        this.globalService.showContextMenu(event)

        const leadSubMenuItems: any = this.leadStatuses.map((status) => {
            return {
                label: status,
                action: () => this.updateLeadStatus(adminClient, status as LeadStatus),
            }
        })

        this.globalService.contextMenuItems.push({
            label: 'Update Lead Status',
            subMenuItems: leadSubMenuItems,
        })

        // Show user assignment submenu - sales managers can reassign but not to blank
        const leadUserSubMenuItems: any = this.crmAssignees.map((assignee) => {
            return {
                label: assignee.displayName,
                action: () => this.updateLeadUser(adminClient, assignee.$key),
            }
        })
        // Only admins can set to "None" (unassigned)
        if (!this.isSalesManagerMode) {
            leadUserSubMenuItems.push({
                label: 'None',
                action: () => this.updateLeadUser(adminClient, null),
            })
        }
        this.globalService.contextMenuItems.push({
            label: 'Update Lead User',
            subMenuItems: leadUserSubMenuItems,
        })
    }

    async updateLeadStatus(adminClient: AdminClient, status: LeadStatus) {
        const leadClosedReasons = Object.values(LeadClosedReason)

        if (status === LeadStatus.CLOSED) {
            const { value: reason } = await Swal.fire({
                title: 'Select a reason for closing',
                input: 'select',
                inputOptions: leadClosedReasons.reduce((options: any, reason: any) => {
                    options[reason] = reason
                    return options
                }, {}),
                inputPlaceholder: 'Select a reason',
                showCancelButton: true,
            })

            if (reason) {
                adminClient.leadClosedReason = reason
                await this.adminClientService.update(adminClient)
            }
        } else if (status === LeadStatus.CONTACTED && (!adminClient.userResponsible || !adminClient.numberOfBays)) {
            const assigneeOptions = this.crmAssignees.map((assignee) => `<option value="${assignee.$key}">${assignee.displayName}</option>`).join('')
            const { value: formValues } = await Swal.fire({
                title: 'Update Details',
                html: `
                <div class="form-group">
                    <label for="user-select">Select a user responsible:</label>
                    <select id="user-select" class="form-control">
                        ${assigneeOptions}
                    </select>
                </div>
                <div class="form-group mt-3">
                    <label for="bays-input">Enter number of bays:</label>
                    <input id="bays-input" type="number" class="form-control" placeholder="Number of bays" value="${adminClient.numberOfBays || ''}">
                </div>
                <div class="form-group mt-3">
                    <label for="months-input">Enter number of months:</label>
                    <input id="months-input" type="number" class="form-control" placeholder="Number of months (optional)" value="${adminClient.monthsRequired || ''}">
                </div>
            `,
                focusConfirm: false,
                showCancelButton: true,
                preConfirm: () => {
                    const user = (document.getElementById('user-select') as HTMLSelectElement).value
                    const bays = (document.getElementById('bays-input') as HTMLInputElement).value
                    const months = (document.getElementById('months-input') as HTMLInputElement).value
                    return { user, bays, months }
                },
            })

            if (formValues) {
                if (formValues.user) {
                    adminClient.userResponsible = formValues.user
                }
                if (formValues.bays) {
                    adminClient.numberOfBays = parseInt(formValues.bays, 10)
                }
                if (formValues.months) {
                    adminClient.monthsRequired = parseInt(formValues.months, 10)
                }
            }
        }

        adminClient.leadStatus = status
        await this.adminClientService.update(adminClient)
    }

    async updateLeadUser(adminClient: AdminClient, user: any) {
        adminClient.userResponsible = user
        await this.adminClientService.update(adminClient)
    }

    callPhone(phoneNumber: string) {
        window.open(`tel:${phoneNumber.replace(/\s+/g, '')}`, '_blank')
    }

    sendWhatsApp(phoneNumber: string) {
        window.open(`https://wa.me/${phoneNumber.replace(/\s+/g, '')}`, '_blank')
    }

    sendEmail(email: string) {
        window.open(`mailto:${email}`, '_blank')
    }

    navigateToReporting(source: string | undefined, campaign: string | undefined) {
        const queryParams: any = {}
        if (source) {
            queryParams.source = source
        }
        if (campaign) {
            queryParams.campaign = campaign
        }
        this.router.navigate(['/admin/dashboard'], { queryParams })
    }

    async toggleStar(adminClient: AdminClient) {
        adminClient.isStarred = !adminClient.isStarred
        await this.adminClientService.update(adminClient)
    }

    getAuthIcon(authType: string): string {
        switch (authType) {
            case 'phone':
                return 'fa-solid fa-phone'
            case 'gmail':
                return 'fa-brands fa-google'
            case 'facebook':
                return 'fa-brands fa-facebook-f'
            case 'email':
                return 'fa-solid fa-envelope'
            default:
                return 'fa-solid fa-user'
        }
    }

    getLeadStatusColor(status: string): string {
        switch (status) {
            case 'NEW':
                return 'info'
            case 'CONTACTED':
                return 'warning'
            case 'CONVERTED':
                return 'success'
            case 'CLOSED':
                return 'secondary'
            default:
                return 'secondary'
        }
    }

    getLeadValueTier(client: AdminClient): 'HIGH' | 'MEDIUM' | 'LOW' {
        return this.globalVariablesService.getLeadValueTier(client.numberOfBays || 0, client.monthsRequired || 0)
    }

    getLeadValue(client: AdminClient): number {
        return this.globalVariablesService.getLeadValueAmount(client.numberOfBays || 0, client.monthsRequired || 0)
    }

    getFullName(client: AdminClient): string {
        const firstName = client.firstName || 'Not set'
        const lastName = client.lastName || ''
        return (firstName + ' ' + lastName).trim()
    }

    getAssigneeDisplayName(userResponsible: string | undefined): string {
        if (!userResponsible) return ''
        const assignee = this.crmAssigneeMap[userResponsible]
        // Only return the display name if it's a valid CRM assignee
        // Old enum values will return empty string
        return assignee ? assignee.displayName : ''
    }

    getAssigneeBadgeColor(userResponsible: string | undefined): string {
        if (!userResponsible) return '#6c757d'
        const assignee = this.crmAssigneeMap[userResponsible]
        return assignee?.badgeColor || '#6c757d'
    }

    isValidAssignee(userResponsible: string | undefined): boolean {
        if (!userResponsible) return false
        return !!this.crmAssigneeMap[userResponsible]
    }

    private getSubEntries(client: AdminClient): any[] {
        if (!client.subscriptionSummaries) return []
        return Object.values(client.subscriptionSummaries)
    }

    getActiveSubCount(client: AdminClient): number {
        return this.getSubEntries(client).filter((s) => s.status === 'Active').length
    }

    hasActiveSub(client: AdminClient): boolean {
        return this.getActiveSubCount(client) > 0
    }

    getActiveSubExpiry(client: AdminClient): any {
        const active = this.getSubEntries(client).find((s) => s.status === 'Active')
        return active?.endDate || null
    }

    hasFutureSub(client: AdminClient): boolean {
        const now = new Date()
        return this.getSubEntries(client).some((s) => {
            if (s.status !== 'Active') return false
            const start = s.startDate instanceof Date ? s.startDate : s.startDate?.seconds ? new Date(s.startDate.seconds * 1000) : null
            return start && start > now
        })
    }

    getCompletedSubCount(client: AdminClient): number {
        return this.getSubEntries(client).filter((s) => s.status === 'Completed').length
    }

    getBreachedSubCount(client: AdminClient): number {
        return this.getSubEntries(client).filter((s) => s.status === 'Client Breached Non Payment').length
    }
}
